import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Receipt, PlayCircle, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock, EyeOff, Search, Save, Trash2, Loader2 } from "lucide-react";
import AutoInvoiceForecastWidget from "@/components/AutoInvoiceForecastWidget";

const STATUS_META = {
  DONE: { icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Done" },
  SHADOW: { icon: EyeOff, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "Shadow" },
  BLOCKED_PO_MISSING: { icon: XCircle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Blocked: PO missing" },
  BLOCKED_PO_EXPIRED: { icon: XCircle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Blocked: PO expired" },
  BLOCKED_CLIENT_NOT_VALIDATED: { icon: XCircle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Blocked: client not validated" },
  BLOCKED_CONTACT_EMAIL_MISSING: { icon: XCircle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Blocked: no contact email" },
  ERROR: { icon: AlertTriangle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Error" },
  // Iter-139: subcontractor CRA auto-export statuses
  DONE_CRA_EXPORT: { icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "CRA exported (waiting J+N)" },
  SHADOW_CRA_EXPORT: { icon: EyeOff, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "Shadow: would export CRA" },
  ERROR_CRA_EXPORT: { icon: AlertTriangle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "CRA export error" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { icon: Clock, color: "text-zinc-700", bg: "bg-zinc-50", border: "border-zinc-200", label: status };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-mono rounded-md border ${meta.color} ${meta.bg} ${meta.border}`}>
      <Icon size={12} /> {meta.label}
    </span>
  );
}

export default function AutoInvoiceDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("history"); // "history" | "config" | "settings"
  // Iter-166 — Settings tab state (auto-invoice global toggles + log purge config)
  const [settingsForm, setSettingsForm] = useState({
    auto_invoice_enabled: false,
    auto_invoice_shadow_mode: false,
    auto_invoice_log_purge_days: 30,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [purgingResolved, setPurgingResolved] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [running, setRunning] = useState(false);
  const [missions, setMissions] = useState([]);
  const [selectedMission, setSelectedMission] = useState("");
  const now = new Date();
  const [runYear, setRunYear] = useState(now.getFullYear());
  const [runMonth, setRunMonth] = useState(now.getMonth() + 1);
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [savingClientId, setSavingClientId] = useState(null);
  const [savingMissionId, setSavingMissionId] = useState(null);
  const [savingGlobals, setSavingGlobals] = useState(false);
  const [configFilter, setConfigFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [logR, missR, setR, cliR] = await Promise.all([
        api.get("/billing/auto-invoice/log?limit=500"),
        api.get("/missions"),
        api.get("/settings"),
        api.get("/clients"),
      ]);
      setItems(logR.data?.items || []);
      const ms = Array.isArray(missR.data) ? missR.data : (missR.data?.items || []);
      setMissions(ms.filter((m) => m.status === "ACTIVE"));
      setSettings(setR.data || {});
      // Iter-166 — initialize Settings-tab form from the loaded settings.
      setSettingsForm({
        auto_invoice_enabled: !!setR.data?.auto_invoice_enabled,
        auto_invoice_shadow_mode: !!setR.data?.auto_invoice_shadow_mode,
        auto_invoice_log_purge_days: Number(setR.data?.auto_invoice_log_purge_days ?? 30),
      });
      // Iter-162: derive a stable `validated` boolean from the canonical `status` field.
      // The backend exposes `status: "ACTIVE" | "PENDING_VALIDATION"` but never a `validated`
      // attribute, so the previous check `c.validated` was always falsy and every row
      // rendered the "non validé" badge regardless of reality.
      const cliRaw = Array.isArray(cliR.data) ? cliR.data : (cliR.data?.items || []);
      setClients(cliRaw.map((c) => ({ ...c, validated: c.status !== "PENDING_VALIDATION" })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== "ALL" && it.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${it.mission_id} ${it.year}-${it.month} ${it.reason || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, filter, statusFilter]);

  const runOnce = async (force = false) => {
    setRunning(true);
    try {
      const payload = {
        year: parseInt(runYear, 10),
        month: parseInt(runMonth, 10),
        force,
      };
      if (selectedMission) payload.mission_id = selectedMission;
      const r = await api.post("/billing/auto-invoice/run", payload);
      const results = r.data?.results || [];
      const counts = results.reduce((acc, x) => { acc[x.status] = (acc[x.status] || 0) + 1; return acc; }, {});
      toast.success(`Run complete — ${Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(" · ")}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  // Iter-154: Re-run a single (mission, year, month) directly from a blocked log row
  // — once the blocker is fixed (client validated, PO renewed, contact email added),
  // the user can retrigger the auto-invoice without waiting 24h for the cron.
  const reRun = async (mission_id, year, month) => {
    setRunning(true);
    try {
      const r = await api.post("/billing/auto-invoice/run", { mission_id, year, month, force: true });
      const results = r.data?.results || [];
      const last = results[0] || {};
      if (last.status && last.status.startsWith("DONE")) {
        toast.success(`Re-run OK — invoice ${last.invoice_id || "generated"}`);
      } else if (last.status && last.status.startsWith("BLOCKED")) {
        toast.error(`Re-run encore bloqué : ${last.reason || last.status}`, { duration: 8000 });
      } else {
        toast.message(`Re-run: ${last.status || "no result"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-run failed");
    } finally { setRunning(false); }
  };

  if (user?.role !== "CEO" && user?.role !== "ADMIN") {
    return <div className="p-10 text-center text-zinc-600">Admin only.</div>;
  }

  const enabled = !!settings?.auto_invoice_enabled;
  const shadow = !!settings?.auto_invoice_shadow_mode;
  const craExportEnabled = settings?.auto_cra_export_enabled !== false;

  const saveGlobals = async (patch) => {
    setSavingGlobals(true);
    try {
      const r = await api.put("/settings", patch);
      setSettings(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingGlobals(false); }
  };

  const setClientFlag = async (clientId, key, value) => {
    setSavingClientId(clientId);
    try {
      await api.patch(`/clients/${clientId}`, { [key]: value });
      setClients((prev) => prev.map((c) => c.client_id === clientId ? { ...c, [key]: value } : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingClientId(null); }
  };

  // Iter-162 — one-click "Validate client now" CTA right from the History row.
  // Uses the existing CEO endpoint (Iter-156-ter) that flips status PENDING_VALIDATION → ACTIVE
  // without needing a Drive folder, then auto re-runs the auto-invoice for that
  // mission/period so the user gets immediate feedback (DONE/SHADOW or next blocker).
  // If the re-run fails after a successful validation, surface a single combined
  // error so the operator knows the client IS validated even if the run failed.
  const validateClientAndRerun = async (clientId, missionId, year, month) => {
    if (!clientId) {
      toast.error("Mission has no client_id — cannot auto-validate");
      return;
    }
    try {
      await api.post(`/clients/${clientId}/validate-now`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
      return;
    }
    toast.success("Client validated · re-running auto-invoice…");
    try {
      await reRun(missionId, year, month);
    } catch (e) {
      toast.error(`Validated, but re-run failed: ${e?.response?.data?.detail || e?.message || "unknown"}`);
    }
  };

  const setMissionFlag = async (missionId, key, value) => {
    setSavingMissionId(missionId);
    try {
      await api.patch(`/missions/${missionId}`, { [key]: value });
      setMissions((prev) => prev.map((m) => m.mission_id === missionId ? { ...m, [key]: value } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingMissionId(null); }
  };

  const TriStateSelect = ({ value, onChange, disabled, testid }) => (
    <select
      value={value === null || value === undefined ? "inherit" : (value ? "on" : "off")}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "inherit" ? null : v === "on");
      }}
      disabled={disabled}
      data-testid={testid}
      className="h-8 px-2 text-xs border border-zinc-200 rounded-md bg-white disabled:opacity-50"
    >
      <option value="inherit">Inherit</option>
      <option value="on">ON</option>
      <option value="off">OFF</option>
    </select>
  );

  const filteredClients = clients.filter((c) => !configFilter || c.name?.toLowerCase().includes(configFilter.toLowerCase()));
  const filteredMissions = missions.filter((m) => !configFilter || m.title?.toLowerCase().includes(configFilter.toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10 space-y-6" data-testid="auto-invoice-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Admin · Auto-invoice</div>
          <h1 className="font-heading font-bold text-3xl text-zinc-900">End-of-month auto-invoice</h1>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[11px] font-mono px-2 py-1 rounded-md border ${enabled ? (shadow ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200") : "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>
              {enabled ? (shadow ? "LIVE · Shadow mode (no emails)" : "LIVE · Sending emails") : "Globally disabled"}
            </span>
          </div>
        </div>
        <button onClick={load} data-testid="ai-refresh-btn" className="inline-flex items-center gap-2 px-4 h-10 border border-zinc-200 text-sm rounded-md hover:bg-zinc-50">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Tabs ------------------------------------------------------------- */}
      <div className="flex items-center gap-2 border-b border-zinc-200">
        <button
          onClick={() => setTab("history")} data-testid="ai-tab-history"
          className={`px-4 h-10 text-sm font-medium border-b-2 -mb-[2px] ${tab === "history" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
        >History & Run</button>
        <button
          onClick={() => setTab("config")} data-testid="ai-tab-config"
          className={`px-4 h-10 text-sm font-medium border-b-2 -mb-[2px] ${tab === "config" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
        >Configuration</button>
        {user?.role === "CEO" && (
          <button
            onClick={() => setTab("settings")} data-testid="ai-tab-settings"
            className={`px-4 h-10 text-sm font-medium border-b-2 -mb-[2px] ${tab === "settings" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
          >Settings</button>
        )}
      </div>

      {tab === "history" && (
      <>
      {/* Run-now panel ---------------------------------------------------- */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4" data-testid="ai-run-panel">
        <div className="flex items-center gap-3 text-zinc-900">
          <PlayCircle size={18} />
          <h2 className="font-heading font-bold text-lg">Run now</h2>
        </div>
        <p className="text-xs text-zinc-500 leading-relaxed">
          Trigger the flow for a single mission or all missions for a given period.
          <strong> Force</strong> bypasses the global kill-switch and opt-in cascade — useful for a CEO-controlled pilot.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs text-zinc-600">Year</label>
            <input type="number" value={runYear} onChange={(e) => setRunYear(e.target.value)} data-testid="ai-run-year"
                   className="w-full h-10 px-3 border border-zinc-300 rounded-md font-mono text-sm" />
          </div>
          <div>
            <label className="text-xs text-zinc-600">Month</label>
            <input type="number" min="1" max="12" value={runMonth} onChange={(e) => setRunMonth(e.target.value)} data-testid="ai-run-month"
                   className="w-full h-10 px-3 border border-zinc-300 rounded-md font-mono text-sm" />
          </div>
          <div>
            <label className="text-xs text-zinc-600">Mission (optional)</label>
            <select value={selectedMission} onChange={(e) => setSelectedMission(e.target.value)} data-testid="ai-run-mission"
                    className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
              <option value="">— All missions —</option>
              {missions.map((m) => <option key={m.mission_id} value={m.mission_id}>{m.title}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => runOnce(false)} disabled={running} data-testid="ai-run-btn"
                    className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
              <PlayCircle size={14} /> {running ? "Running…" : "Run"}
            </button>
            <button onClick={() => runOnce(true)} disabled={running || !selectedMission} data-testid="ai-force-btn"
                    title={selectedMission ? "Bypass kill-switch and opt-in cascade for this mission" : "Select a mission to enable Force"}
                    className="inline-flex items-center gap-2 px-3 h-10 border border-orange-300 bg-orange-50 text-orange-700 text-xs rounded-md hover:bg-orange-100 disabled:opacity-40">
              Force
            </button>
          </div>
        </div>
      </div>

      {/* History table ---------------------------------------------------- */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="ai-history-block">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-zinc-900">
            <Receipt size={16} />
            <h2 className="font-heading font-bold">History · {items.length} runs</h2>
          </div>
          <div className="flex-1"></div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={filter} onChange={(e) => setFilter(e.target.value)}
              data-testid="ai-filter-input"
              placeholder="Filter (mission, reason…)"
              className="pl-9 pr-3 h-9 border border-zinc-200 rounded-md text-sm w-64"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ai-filter-status"
                  className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white">
            <option value="ALL">All statuses</option>
            {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] font-mono uppercase tracking-wider text-zinc-600">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Mission</th>
                <th className="px-4 py-2 text-left">Period</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Invoice</th>
                <th className="px-4 py-2 text-left">Reason / actor</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No runs yet. Trigger one above or wait for the next cron tick (04:00 UTC).</td></tr>
              ) : (
                filtered.map((it, i) => {
                  const m = missions.find((mm) => mm.mission_id === it.mission_id);
                  return (
                    <tr key={i} data-testid={`ai-row-${it.mission_id}-${it.year}-${it.month}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2 font-mono text-[11px] text-zinc-600 whitespace-nowrap">{(it.executed_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="px-4 py-2">
                        <div className="font-medium text-zinc-900">{m?.title || it.mission_id}</div>
                        <div className="text-[11px] font-mono text-zinc-500">{it.mission_id}</div>
                      </td>
                      <td className="px-4 py-2 font-mono text-zinc-700 whitespace-nowrap">{it.year}-{String(it.month).padStart(2, "0")}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <StatusPill status={it.status} />
                        {it.blocker_resolved && (
                          <div
                            data-testid={`ai-blocker-resolved-${it.mission_id}-${it.year}-${it.month}`}
                            title="Le bloqueur (client non validé) a été résolu depuis ce run. Cliquez sur Re-run after fix pour rejouer la facturation."
                            className="mt-1 inline-flex items-center gap-1 px-2 h-5 rounded text-[10px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200"
                          >
                            <CheckCircle2 size={10} /> Bloqueur résolu — Re-run pending
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-[12px] text-zinc-700">{it.invoice_id || "—"}</td>
                      <td className="px-4 py-2 text-xs text-zinc-600 max-w-md break-words">
                        <div>{it.reason || it.actor}</div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {it.fix_url && (
                            <a
                              href={it.fix_url}
                              data-testid={`ai-fix-${it.mission_id}-${it.year}-${it.month}`}
                              className="inline-flex items-center gap-1 px-2 h-6 bg-amber-100 text-amber-900 hover:bg-amber-200 rounded text-[11px] font-medium border border-amber-200"
                            >
                              → {it.fix_label || "Corriger"}
                            </a>
                          )}
                          {it.status && it.status.startsWith("BLOCKED_") && (
                            <button
                              onClick={() => reRun(it.mission_id, it.year, it.month)}
                              disabled={running}
                              data-testid={`ai-rerun-${it.mission_id}-${it.year}-${it.month}`}
                              className="inline-flex items-center gap-1 px-2 h-6 bg-emerald-100 text-emerald-900 hover:bg-emerald-200 rounded text-[11px] font-medium border border-emerald-200 disabled:opacity-50"
                              title="Re-déclencher l'auto-invoice pour cette mission/période"
                            >
                              <RefreshCw size={11} /> Re-run after fix
                            </button>
                          )}
                          {it.status === "BLOCKED_CLIENT_NOT_VALIDATED" && (
                            <button
                              onClick={() => validateClientAndRerun(m?.client_id, it.mission_id, it.year, it.month)}
                              disabled={running}
                              data-testid={`ai-validate-client-${it.mission_id}-${it.year}-${it.month}`}
                              className="inline-flex items-center gap-1 px-2 h-6 bg-blue-100 text-blue-900 hover:bg-blue-200 rounded text-[11px] font-medium border border-blue-200 disabled:opacity-50"
                              title="Forcer la validation du client (CEO override) puis relancer l'auto-invoice immédiatement"
                            >
                              <CheckCircle2 size={11} /> Validate client now
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card view */}
        <div className="md:hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No runs yet. Trigger one above or wait for the next cron tick (04:00 UTC).</div>
          ) : (
            filtered.map((it, i) => {
              const m = missions.find((mm) => mm.mission_id === it.mission_id);
              return (
                <div key={`m-${i}`} data-testid={`ai-row-m-${it.mission_id}-${it.year}-${it.month}`} className="border-t border-zinc-100 px-4 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-zinc-900 break-words">{m?.title || it.mission_id}</div>
                      <div className="text-[11px] font-mono text-zinc-500 truncate">{it.mission_id}</div>
                    </div>
                    <div className="font-mono text-[11px] text-zinc-700 whitespace-nowrap shrink-0">{it.year}-{String(it.month).padStart(2, "0")}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill status={it.status} />
                    {it.blocker_resolved && (
                      <span className="inline-flex items-center gap-1 px-2 h-5 rounded text-[10px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200">
                        <CheckCircle2 size={10} /> Résolu
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500">
                    {(it.executed_at || "").slice(0, 19).replace("T", " ")}
                    {it.invoice_id && <span className="ml-2 text-zinc-700">· {it.invoice_id}</span>}
                  </div>
                  {(it.reason || it.actor) && (
                    <div className="text-xs text-zinc-600 break-words">{it.reason || it.actor}</div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {it.fix_url && (
                      <a href={it.fix_url} className="inline-flex items-center gap-1 px-2 h-7 bg-amber-100 text-amber-900 rounded text-[11px] font-medium border border-amber-200">
                        → {it.fix_label || "Corriger"}
                      </a>
                    )}
                    {it.status && it.status.startsWith("BLOCKED_") && (
                      <button onClick={() => reRun(it.mission_id, it.year, it.month)} disabled={running} className="inline-flex items-center gap-1 px-2 h-7 bg-emerald-100 text-emerald-900 rounded text-[11px] font-medium border border-emerald-200 disabled:opacity-50">
                        <RefreshCw size={11} /> Re-run
                      </button>
                    )}
                    {it.status === "BLOCKED_CLIENT_NOT_VALIDATED" && (
                      <button onClick={() => validateClientAndRerun(m?.client_id, it.mission_id, it.year, it.month)} disabled={running} className="inline-flex items-center gap-1 px-2 h-7 bg-blue-100 text-blue-900 rounded text-[11px] font-medium border border-blue-200 disabled:opacity-50">
                        <CheckCircle2 size={11} /> Validate
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      </>
      )}

      {tab === "config" && (
      <div className="space-y-6" data-testid="ai-config-block">
        {/* Iter-195r — Forecast widget: mini-calendar showing what will fire
            on each upcoming day so the CEO can anticipate the auto-invoice
            traffic and optionally force-send a specific (mission, period). */}
        <AutoInvoiceForecastWidget onForceRan={async () => { await load(); }} />

        {/* Global toggles -------------------------------------------------- */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-5">
          <h2 className="font-heading font-bold text-lg text-zinc-900">Global toggles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={enabled} onChange={(e) => saveGlobals({ auto_invoice_enabled: e.target.checked })} disabled={savingGlobals} data-testid="cfg-global-invoice" className="mt-1" />
              <div>
                <div className="text-sm font-medium">Invoice auto-send</div>
                <div className="text-xs text-zinc-500">Generate + email the invoice when all CRA approved (and J+N elapsed for sub-traitants).</div>
              </div>
            </label>
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={shadow} onChange={(e) => saveGlobals({ auto_invoice_shadow_mode: e.target.checked })} disabled={savingGlobals || !enabled} data-testid="cfg-global-shadow" className="mt-1" />
              <div>
                <div className="text-sm font-medium">Shadow mode</div>
                <div className="text-xs text-zinc-500">Generate PDFs but DO NOT send. Recommended for the first month of rollout.</div>
              </div>
            </label>
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={craExportEnabled} onChange={(e) => saveGlobals({ auto_cra_export_enabled: e.target.checked })} disabled={savingGlobals} data-testid="cfg-global-craexport" className="mt-1" />
              <div>
                <div className="text-sm font-medium">CRA auto-export (sub-traitants)</div>
                <div className="text-xs text-zinc-500">When OFF, the manager must export the CRA manually. Invoice flow resumes after export + J+N.</div>
              </div>
            </label>
          </div>

          {/* Iter-195q — Global send-mode (IMMEDIATE vs END_OF_MONTH).
              Default is END_OF_MONTH: the auto-invoice waits until the last
              day of the calendar month before firing (or fires immediately
              for past months — late CRA validations don't get stuck). */}
          <div className="border-t border-zinc-100 pt-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-sm font-medium">Send mode (timing)</div>
                <div className="text-xs text-zinc-500 max-w-xl">
                  <strong>End of month</strong> (recommandé) : la facture s'envoie le dernier jour du mois (ou immédiatement si les CRA validés concernent un mois antérieur).<br/>
                  <strong>Immediate</strong> : la facture part dès que tous les CRA de la mission/mois sont approuvés, sans attendre.
                </div>
              </div>
              <select
                value={settings?.auto_invoice_send_mode || "END_OF_MONTH"}
                onChange={(e) => saveGlobals({ auto_invoice_send_mode: e.target.value })}
                disabled={savingGlobals || !enabled}
                data-testid="cfg-global-send-mode"
                className="h-9 border border-zinc-200 rounded-md text-sm px-2"
              >
                <option value="END_OF_MONTH">End of month (default)</option>
                <option value="IMMEDIATE">Immediate</option>
              </select>
            </div>
          </div>
        </div>

        {/* Filter --------------------------------------------------------- */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={configFilter} onChange={(e) => setConfigFilter(e.target.value)} placeholder="Filter clients/missions…" data-testid="cfg-filter"
                 className="pl-9 pr-3 h-9 border border-zinc-200 rounded-md text-sm w-full md:w-96" />
        </div>

        {/* Per-client overrides ------------------------------------------- */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-heading font-bold text-lg">Per-client overrides ({filteredClients.length})</h2>
            {user?.role === "CEO" && (() => {
              const stuckCount = clients.filter((c) => !c.validated).length;
              if (stuckCount === 0) return null;
              return (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Valider en bulk les ${stuckCount} client(s) bloqués en PENDING_VALIDATION ? Action irréversible — chacun passera ACTIVE sans dossier Drive (utilise le wizard pour ça).`)) return;
                    try {
                      const r = await api.post("/clients/bulk-validate-now");
                      toast.success(`${r.data?.validated ?? 0} client(s) validés`);
                      load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Bulk validate failed");
                    }
                  }}
                  data-testid="cfg-bulk-validate-stuck"
                  className="inline-flex items-center gap-1 px-3 h-8 bg-blue-600 text-white rounded text-xs font-semibold hover:bg-blue-700"
                  title={`${stuckCount} client(s) PENDING_VALIDATION — un seul clic pour tous les passer ACTIVE`}
                >
                  <CheckCircle2 size={12} /> Validate all stuck clients ({stuckCount})
                </button>
              );
            })()}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[11px] font-mono uppercase tracking-wider text-zinc-600">
                <tr>
                  <th className="px-4 py-2 text-left">Client</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Invoice auto-send</th>
                  <th className="px-4 py-2 text-left">CRA auto-export</th>
                  <th className="px-4 py-2 text-left" title="Override the global send-mode at the client level (Mission overrides win).">Send mode</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((c) => (
                  <tr key={c.client_id} data-testid={`cfg-client-${c.client_id}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2 font-medium">{c.name}</td>
                    <td className="px-4 py-2 text-xs text-zinc-600">{c.is_subcontractor ? "Sub-traitant" : "Client final"}{c.validated ? "" : " · ⚠️ non validé"}</td>
                    <td className="px-4 py-2"><TriStateSelect value={c.auto_invoice_enabled} onChange={(v) => setClientFlag(c.client_id, "auto_invoice_enabled", v)} disabled={savingClientId === c.client_id} testid={`cfg-client-invoice-${c.client_id}`} /></td>
                    <td className="px-4 py-2"><TriStateSelect value={c.auto_cra_export_enabled} onChange={(v) => setClientFlag(c.client_id, "auto_cra_export_enabled", v)} disabled={savingClientId === c.client_id} testid={`cfg-client-craexport-${c.client_id}`} /></td>
                    {/* Iter-195q — Per-client send-mode override (inherit | IMMEDIATE | END_OF_MONTH). */}
                    <td className="px-4 py-2">
                      <select
                        value={c.auto_invoice_send_mode || ""}
                        onChange={(e) => setClientFlag(c.client_id, "auto_invoice_send_mode", e.target.value || null)}
                        disabled={savingClientId === c.client_id || user?.role !== "CEO"}
                        data-testid={`cfg-client-sendmode-${c.client_id}`}
                        className="h-7 border border-zinc-200 rounded text-xs px-1.5 bg-white disabled:bg-zinc-50 disabled:text-zinc-400"
                      >
                        <option value="">Inherit</option>
                        <option value="END_OF_MONTH">End of month</option>
                        <option value="IMMEDIATE">Immediate</option>
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      {!c.validated && user?.role === "CEO" ? (
                        <button
                          onClick={async () => {
                            try {
                              await api.post(`/clients/${c.client_id}/validate-now`);
                              setClients((prev) => prev.map((cc) => cc.client_id === c.client_id ? { ...cc, validated: true } : cc));
                              toast.success(`Client "${c.name}" validated`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Validation failed");
                            }
                          }}
                          data-testid={`cfg-client-validate-${c.client_id}`}
                          className="inline-flex items-center gap-1 px-2 h-7 bg-blue-50 text-blue-900 hover:bg-blue-100 rounded text-[11px] font-medium border border-blue-200"
                          title="Force validate (CEO override) — flips status PENDING_VALIDATION → ACTIVE"
                        >
                          <CheckCircle2 size={11} /> Validate now
                        </button>
                      ) : c.validated ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 size={11} /> Validated</span>
                      ) : (
                        <span className="text-[11px] text-zinc-400">CEO only</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredClients.length === 0 && (<tr><td colSpan={5} className="p-6 text-center text-zinc-500">No clients</td></tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className="md:hidden">
            {filteredClients.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No clients</div>}
            {filteredClients.map((c) => (
              <div key={`m-${c.client_id}`} data-testid={`cfg-client-m-${c.client_id}`} className="border-t border-zinc-100 px-4 py-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-sm break-words flex-1 min-w-0">{c.name}</div>
                  <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 shrink-0">{c.is_subcontractor ? "Sub" : "Final"}</span>
                </div>
                {!c.validated && (
                  <div className="text-[11px] text-amber-700">⚠️ Client non validé</div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-wider font-mono text-zinc-500">Invoice</span>
                  <TriStateSelect value={c.auto_invoice_enabled} onChange={(v) => setClientFlag(c.client_id, "auto_invoice_enabled", v)} disabled={savingClientId === c.client_id} testid={`cfg-client-invoice-m-${c.client_id}`} />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-wider font-mono text-zinc-500">CRA Export</span>
                  <TriStateSelect value={c.auto_cra_export_enabled} onChange={(v) => setClientFlag(c.client_id, "auto_cra_export_enabled", v)} disabled={savingClientId === c.client_id} testid={`cfg-client-craexport-m-${c.client_id}`} />
                </div>
                <div className="flex items-center justify-end pt-1">
                  {!c.validated && user?.role === "CEO" ? (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/clients/${c.client_id}/validate-now`);
                          setClients((prev) => prev.map((cc) => cc.client_id === c.client_id ? { ...cc, validated: true } : cc));
                          toast.success(`Client "${c.name}" validated`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Validation failed");
                        }
                      }}
                      className="inline-flex items-center gap-1 px-2 h-7 bg-blue-50 text-blue-900 rounded text-[11px] font-medium border border-blue-200"
                    >
                      <CheckCircle2 size={11} /> Validate now
                    </button>
                  ) : c.validated ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 size={11} /> Validated</span>
                  ) : (
                    <span className="text-[11px] text-zinc-400">CEO only</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Per-mission overrides ------------------------------------------ */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-heading font-bold text-lg">Per-mission overrides ({filteredMissions.length})</h2>
            <p className="text-[11px] text-zinc-500">Mission ⇒ Client ⇒ Global cascade. "Inherit" = follow the next level up.</p>
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[11px] font-mono uppercase tracking-wider text-zinc-600">
                <tr>
                  <th className="px-4 py-2 text-left">Mission</th>
                  <th className="px-4 py-2 text-left">Client</th>
                  <th className="px-4 py-2 text-left">Invoice auto-send</th>
                  <th className="px-4 py-2 text-left">CRA auto-export</th>
                </tr>
              </thead>
              <tbody>
                {filteredMissions.map((m) => {
                  const cl = clients.find((c) => c.client_id === m.client_id);
                  const isForfait = m.type === "FORFAIT";
                  return (
                    <tr key={m.mission_id} data-testid={`cfg-mission-${m.mission_id}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2 font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{m.title}</span>
                          {isForfait && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200"
                              title="Mission FORFAIT — pas de CRA donc pas d'export CRA. L'auto-invoice reste configurable."
                              data-testid={`cfg-mission-forfait-badge-${m.mission_id}`}
                            >
                              Forfait
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs text-zinc-600">{cl?.name || "—"}{cl?.is_subcontractor && " · sub"}</td>
                      <td className="px-4 py-2"><TriStateSelect value={m.auto_invoice_enabled} onChange={(v) => setMissionFlag(m.mission_id, "auto_invoice_enabled", v)} disabled={savingMissionId === m.mission_id} testid={`cfg-mission-invoice-${m.mission_id}`} /></td>
                      <td className="px-4 py-2">
                        {isForfait ? (
                          <span
                            className="inline-flex items-center text-[11px] font-mono text-zinc-400 italic"
                            data-testid={`cfg-mission-craexport-na-${m.mission_id}`}
                            title="N/A — les missions forfait n'ont pas de CRA donc pas d'export à automatiser"
                          >
                            n/a
                          </span>
                        ) : (
                          <TriStateSelect value={m.auto_cra_export_enabled} onChange={(v) => setMissionFlag(m.mission_id, "auto_cra_export_enabled", v)} disabled={savingMissionId === m.mission_id} testid={`cfg-mission-craexport-${m.mission_id}`} />
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredMissions.length === 0 && (<tr><td colSpan={4} className="p-6 text-center text-zinc-500">No missions</td></tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className="md:hidden">
            {filteredMissions.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No missions</div>}
            {filteredMissions.map((m) => {
              const cl = clients.find((c) => c.client_id === m.client_id);
              const isForfait = m.type === "FORFAIT";
              return (
                <div key={`m-${m.mission_id}`} data-testid={`cfg-mission-m-${m.mission_id}`} className="border-t border-zinc-100 px-4 py-3 space-y-2">
                  <div className="font-medium text-sm break-words flex items-center gap-2 flex-wrap">
                    <span>{m.title}</span>
                    {isForfait && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 shrink-0"
                        title="Mission FORFAIT — pas de CRA"
                        data-testid={`cfg-mission-forfait-badge-m-${m.mission_id}`}
                      >
                        Forfait
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-600">{cl?.name || "—"}{cl?.is_subcontractor && " · sub"}</div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] uppercase tracking-wider font-mono text-zinc-500">Invoice</span>
                    <TriStateSelect value={m.auto_invoice_enabled} onChange={(v) => setMissionFlag(m.mission_id, "auto_invoice_enabled", v)} disabled={savingMissionId === m.mission_id} testid={`cfg-mission-invoice-m-${m.mission_id}`} />
                  </div>
                  {!isForfait && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] uppercase tracking-wider font-mono text-zinc-500">CRA Export</span>
                      <TriStateSelect value={m.auto_cra_export_enabled} onChange={(v) => setMissionFlag(m.mission_id, "auto_cra_export_enabled", v)} disabled={savingMissionId === m.mission_id} testid={`cfg-mission-craexport-m-${m.mission_id}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Iter-166 — Settings tab : kill-switch + shadow + history purge ===== */}
      {tab === "settings" && user?.role === "CEO" && (
      <div className="space-y-6" data-testid="ai-settings-panel">
        {/* Auto-invoice end-of-month automation (moved from Maintenance) */}
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5" data-testid="auto-invoice-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Receipt size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Auto-invoice — end-of-month automation</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            When every collaborator's CRA is <strong>APPROVED</strong> for a (mission, year, month), the platform
            will automatically generate + email the client invoice. Opt-in cascade:
            mission ⇒ client ⇒ this global toggle. Sub-contractor missions are handled in a later phase.
            Safeguards always block the send if the BC/PO is missing or expired, or the client isn't validated.
          </p>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settingsForm.auto_invoice_enabled}
                onChange={(e) => setSettingsForm((p) => ({ ...p, auto_invoice_enabled: e.target.checked }))}
                data-testid="auto-invoice-enabled"
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-zinc-900">Enable auto-invoice globally (kill-switch)</div>
                <div className="text-xs text-zinc-500 mt-0.5">When OFF, nothing runs. No cron, no hooks, no emails. Flip ON to start the workflow.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settingsForm.auto_invoice_shadow_mode}
                onChange={(e) => setSettingsForm((p) => ({ ...p, auto_invoice_shadow_mode: e.target.checked }))}
                data-testid="auto-invoice-shadow"
                className="mt-1"
                disabled={!settingsForm.auto_invoice_enabled}
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-zinc-900">Shadow mode (generate but do NOT send)</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Recommended for the first month of rollout. PDFs are created + logged as <span className="font-mono text-zinc-700">SHADOW</span>, CEO is notified, but NO email leaves the platform. Compare against manual invoices before flipping OFF.
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* History auto-purge (Iter-166) */}
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5" data-testid="auto-invoice-purge-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Trash2 size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">History auto-purge — resolved blockers</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            A daily cron at 03:30 UTC removes <code className="font-mono bg-zinc-100 px-1 rounded">BLOCKED_CLIENT_NOT_VALIDATED</code> rows
            older than the threshold below — but only when the client has since been <strong>validated</strong> (the
            "Bloqueur résolu" badge is shown). Successful runs (DONE, SHADOW) and unresolved blockers are NEVER touched.
            Set to <strong>0</strong> to disable the cron.
          </p>
          <div className="flex items-end gap-4 flex-wrap">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-[0.18em] font-mono text-zinc-500">Purge after (days)</span>
              <input
                type="number"
                min="0"
                max="3650"
                value={settingsForm.auto_invoice_log_purge_days}
                onChange={(e) => setSettingsForm((p) => ({ ...p, auto_invoice_log_purge_days: Math.max(0, Math.min(3650, Number(e.target.value) || 0)) }))}
                data-testid="auto-invoice-log-purge-days"
                className="h-10 w-32 px-3 border border-zinc-200 rounded-md font-mono text-sm"
              />
            </label>
            <button
              onClick={async () => {
                if (!window.confirm("Purger immédiatement toutes les lignes 'Bloqueur résolu' plus anciennes que le seuil ? Action irréversible.")) return;
                setPurgingResolved(true);
                try {
                  const r = await api.post(`/admin/auto-invoice-log/purge-resolved/run?max_age_days=${settingsForm.auto_invoice_log_purge_days || 30}`);
                  toast.success(`${r.data?.deleted ?? 0} ligne(s) purgée(s)`);
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Purge failed");
                } finally { setPurgingResolved(false); }
              }}
              disabled={purgingResolved}
              data-testid="auto-invoice-purge-now"
              className="inline-flex items-center gap-2 px-4 h-10 border border-red-200 text-red-700 text-sm rounded-md hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={14} /> {purgingResolved ? "Purging…" : "Run purge now"}
            </button>
          </div>
        </div>

        {/* Save button (writes both blocks at once) */}
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              setSavingSettings(true);
              try {
                await api.put("/settings", {
                  auto_invoice_enabled: settingsForm.auto_invoice_enabled,
                  auto_invoice_shadow_mode: settingsForm.auto_invoice_shadow_mode,
                  auto_invoice_log_purge_days: Number(settingsForm.auto_invoice_log_purge_days) || 0,
                });
                toast.success(settingsForm.auto_invoice_enabled
                  ? (settingsForm.auto_invoice_shadow_mode ? "Auto-invoice ON (shadow mode)" : "Auto-invoice ON — emails will be sent")
                  : "Auto-invoice disabled");
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Save failed");
              } finally { setSavingSettings(false); }
            }}
            disabled={savingSettings}
            data-testid="auto-invoice-save"
            className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
          >
            <Save size={14} /> {savingSettings ? "Saving…" : "Save"}
          </button>
          {settingsForm.auto_invoice_enabled && (
            <span className={`text-xs font-mono px-2 py-1 rounded-md ${settingsForm.auto_invoice_shadow_mode ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>
              {settingsForm.auto_invoice_shadow_mode ? "SHADOW · no emails sent" : "LIVE · emails will be sent"}
            </span>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

